import React, { useState, useEffect } from 'react';
import { Check, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Asset } from '@/utils/collectionsGraphQL';
import { formatVersionDateUS } from '@/utils/dateUtils';

// Image priority functions
const getPrimaryDisplayImageUrl = (asset: Asset): string => {
  return asset.primary_cropped_url || asset.primary_original_url || asset.primary_image || '';
};

const getSecondaryDisplayImageUrl = (asset: Asset): string => {
  return asset.secondary_cropped_url || asset.secondary_original_url || '';
};

interface CollectionAssetTileProps {
  asset: Asset;
  isSelected: boolean;
  onSelect: () => void;
  onClick: () => void;
}

export const CollectionAssetTile: React.FC<CollectionAssetTileProps> = ({
  asset,
  isSelected,
  onSelect,
  onClick,
}) => {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  // Determine best image URL with fallback chain
  const primaryUrl = getPrimaryDisplayImageUrl(asset);
  const fallbackUrl = asset.primary_original_url || asset.primary_image;

  // Set initial image source
  useEffect(() => {
    setImgSrc(primaryUrl || null);
    setImgError(false);
  }, [primaryUrl]);

  const handleImageError = () => {
    // Try fallback URL if primary fails
    if (!imgError && fallbackUrl && imgSrc !== fallbackUrl) {
      setImgSrc(fallbackUrl);
      setImgError(true);
    }
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Prefer markup URL, then cropped URL
    const url = asset.primary_markup_url || asset.primary_cropped_url || primaryUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Get company name from page data if available
  const companyName = asset.page?.company?.name || asset.slug;
  const hasSecondaryImage = !!getSecondaryDisplayImageUrl(asset);

  return (
    <div
      className={cn(
        "cursor-pointer group rounded-lg overflow-hidden border bg-white transition-all hover:shadow-lg",
        isSelected ? "border-blue-500 ring-2 ring-blue-500/30" : "hover:border-blue-400"
      )}
      onClick={onClick}
    >
      {/* Image container */}
      <div className="relative">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={`${companyName} - ${asset.header || asset.title || ''}`}
            className="w-full aspect-[4/3] object-cover"
            onError={handleImageError}
          />
        ) : (
          <div className="w-full aspect-[4/3] flex items-center justify-center bg-gray-100">
            <span className="text-2xl font-bold text-gray-300 uppercase">
              {asset.slug.substring(0, 2)}
            </span>
          </div>
        )}

        {/* Selection checkbox - top left */}
        <button
          onClick={handleSelectClick}
          className={cn(
            "absolute top-2 left-2 w-5 h-5 rounded flex items-center justify-center transition-all shadow-sm border z-10",
            isSelected
              ? "bg-blue-500 border-blue-500 text-white"
              : "bg-white/90 border-gray-300 text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-white hover:border-gray-400"
          )}
        >
          {isSelected && <Check className="h-3.5 w-3.5" />}
        </button>

        {/* Open in new tab button - top right */}
        <button
          onClick={handleOpenInTab}
          className="absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center transition-all shadow-sm border bg-white/90 border-gray-300 text-gray-600 opacity-0 group-hover:opacity-100 hover:bg-white hover:border-gray-400 hover:text-gray-900 z-10"
          title="Open image in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>

        {/* Comparison indicator badge */}
        {hasSecondaryImage && (
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
            Comparison
          </div>
        )}
      </div>

      {/* Info below image */}
      <div className="p-2 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {companyName}
          </span>
          <span className="text-xs text-gray-500 flex-shrink-0">
            v{asset.version}
          </span>
        </div>
        {asset.header && (
          <p className="text-xs text-gray-600 truncate font-medium">
            {asset.header}
          </p>
        )}
        {asset.subheader && (
          <p className="text-xs text-gray-500 truncate">
            {asset.subheader}
          </p>
        )}
      </div>
    </div>
  );
};
